/**
 * Keeps the kiosk's browser from being used as a browser.
 *
 * The column runs Chromium fullscreen on a touch panel, with nothing but the page between a parker and
 * the rest of the browser. Left alone, a long press selects text and offers copy/paste, a drag of a link
 * or a text selection can split the window into two panes, and a sideways swipe slides or navigates the
 * page. None of those can be undone from a touch panel, so the page refuses them.
 *
 * Scoped away from `body.pay-page`, the same app's settlement page (`/pay/:id`) that runs in the
 * parker's own phone or desktop browser, where all of this is just broken. The class is read when an
 * event fires, not at install, because the route is not known yet at boot.
 *
 * This is the layer that works whatever Chromium version the station runs; the launch flags in
 * RaspberryPi/provisioning/provision.py (`step_payment_kiosk`) are the second layer.
 */
/** Two taps closer than this are a double tap to Chromium's zoom gesture. */
const DOUBLE_TAP_MS = 350;

export function installKioskGuards(doc: Document = document): void {
  const onKiosk = () => !doc.body.classList.contains('pay-page');
  const refuse = (event: Event) => {
    if (onKiosk()) {
      event.preventDefault();
    }
  };

  // Clipboard and the long-press menu.
  for (const type of ['copy', 'cut', 'paste', 'contextmenu']) {
    doc.addEventListener(type, refuse, { capture: true });
  }

  // Drag and drop: dragging a link or a selection towards the window edge is what starts a split view.
  for (const type of ['dragstart', 'dragenter', 'dragover', 'drop']) {
    doc.addEventListener(type, refuse, { capture: true });
  }

  // Text selection, except inside a field where the caret needs it.
  doc.addEventListener(
    'selectstart',
    (event) => {
      const target = event.target as Element | null;
      const inField = target instanceof Element && target.closest('input, textarea') !== null;
      if (onKiosk() && !inField) {
        event.preventDefault();
      }
    },
    { capture: true },
  );

  // Zoom. `--disable-pinch` stops a two-finger pinch only: a DOUBLE TAP still zoomed the page on
  // jku-tfg-pay-001 (2026-10-18), and so does Ctrl+wheel — from a mouse, a touchpad pinch, or the DevTools
  // screencast. Non-passive listeners, or preventDefault is ignored.
  const blocking = { capture: true, passive: false } as const;

  doc.addEventListener(
    'wheel',
    (event) => {
      if (onKiosk() && event.ctrlKey) {
        event.preventDefault();
      }
    },
    blocking,
  );

  // A second finger never means anything on this screen; refusing it stops any pinch the flag lets through.
  for (const type of ['touchstart', 'touchmove'] as const) {
    doc.addEventListener(
      type,
      (event) => {
        if (onKiosk() && event.touches.length > 1) {
          event.preventDefault();
        }
      },
      blocking,
    );
  }

  // Double tap: cancelling the second tap's touchend is what stops the zoom, but it also cancels that tap's
  // click — and two quick taps on the plate keyboard ("LL") are real input. So the click is delivered by
  // hand instead, and only the zoom is lost.
  let lastTapAt = -Infinity;
  doc.addEventListener(
    'touchend',
    (event) => {
      if (!onKiosk() || event.touches.length > 0) {
        return;
      }
      const now = event.timeStamp;
      if (now - lastTapAt < DOUBLE_TAP_MS) {
        event.preventDefault();
        (event.target as HTMLElement | null)?.click?.();
        lastTapAt = -Infinity;
        return;
      }
      lastTapAt = now;
    },
    blocking,
  );

  // Keyboard shortcuts, for a station with a keyboard plugged in. The page cannot stop the ones the
  // browser reserves for itself (new tab / window / close); it can stop copy, paste, select-all, print.
  doc.addEventListener(
    'keydown',
    (event) => {
      if (onKiosk() && (event.ctrlKey || event.metaKey) && event.key.length === 1) {
        event.preventDefault();
      }
    },
    { capture: true },
  );
}
